/**
 * LocationSelector
 *
 * A reusable, fully-controlled Country → State cascading dropdown.
 * Works with BOTH:
 *   1. Manual selection from dropdowns
 *   2. Google AddressAutocomplete — when Google sets country/state text on
 *      the parent, this component fuzzy-matches the name against the API list
 *      and auto-corrects + loads the matching states automatically.
 *
 * Usage:
 *   <LocationSelector
 *     country={formData.country}
 *     state={formData.state}
 *     onCountryChange={(name) => setFormData(p => ({ ...p, country: name, state: '' }))}
 *     onStateChange={(name) => setFormData(p => ({ ...p, state: name }))}
 *     countryError={fieldErrors.country}
 *     stateError={fieldErrors.state}
 *     required
 *   />
 */

import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  SxProps,
  Theme,
} from '@mui/material'
import api from '../../services/api'

// ─── Data Shapes ────────────────────────────────────────────────────────────

interface CountryOption {
  _id: string
  name: string
  abbreviation?: string
}

interface StateOption {
  _id: string
  name: string
  abbreviation?: string
  countryId?: string
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface LocationSelectorProps {
  /** Currently selected country name (controlled) */
  country: string
  /** Currently selected state name (controlled) */
  state: string

  /** Called with the country name when the user picks a country */
  onCountryChange: (countryName: string) => void
  /** Called with the state name when the user picks a state */
  onStateChange: (stateName: string) => void

  /** Validation error messages */
  countryError?: string
  stateError?: string

  /** Whether fields are required (shows * label) */
  required?: boolean

  /** Whether the selects are disabled (e.g., read-only mode) */
  disabled?: boolean

  /**
   * Accent colour for focused borders.
   * Defaults to '#3BAE78' (patient green). Pass '#2E7DAF' for therapist blue.
   */
  accentColor?: string

  /** Additional sx applied to the root wrapper Box */
  sx?: SxProps<Theme>
  /** Option to render only country or state dropdown */
  renderField?: 'country' | 'state'
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Normalize a string for fuzzy comparison */
const norm = (s: string) => s.toLowerCase().trim()

/**
 * Find the best matching country from the API list for a given name.
 * Priority: exact match → includes match → word-overlap match
 */
function fuzzyFindCountry(
  name: string,
  list: CountryOption[]
): CountryOption | undefined {
  if (!name || list.length === 0) return undefined
  const n = norm(name)

  // 1. Exact
  let found = list.find(c => norm(c.name) === n)
  if (found) return found

  // 2. One contains the other  (e.g. "United States" ↔ "United States of America")
  found = list.find(c => norm(c.name).includes(n) || n.includes(norm(c.name)))
  if (found) return found

  // 3. Abbreviation match (e.g. "US" → country.abbreviation === "US")
  found = list.find(
    c => c.abbreviation && norm(c.abbreviation) === n
  )
  return found
}

/**
 * Find the best matching state from the API list for a given name.
 */
function fuzzyFindState(
  name: string,
  list: StateOption[]
): StateOption | undefined {
  if (!name || list.length === 0) return undefined
  const n = norm(name)

  let found = list.find(s => norm(s.name) === n)
  if (found) return found

  found = list.find(s => norm(s.name).includes(n) || n.includes(norm(s.name)))
  if (found) return found

  found = list.find(
    s => s.abbreviation && norm(s.abbreviation) === n
  )
  return found
}

// ─── Shared select sx factory ────────────────────────────────────────────────

const selectSx = (accentColor: string, disabled?: boolean) => ({
  bgcolor: disabled ? 'grey.50' : 'white',
  borderRadius: 2,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.300' },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: disabled ? 'grey.300' : 'grey.400',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: accentColor,
    borderWidth: 2,
  },
})

// ─── Component ───────────────────────────────────────────────────────────────

const LocationSelector: React.FC<LocationSelectorProps> = ({
  country,
  state,
  onCountryChange,
  onStateChange,
  countryError,
  stateError,
  required = false,
  disabled = false,
  accentColor = '#3BAE78',
  sx,
  renderField,
}) => {
  const [countries, setCountries] = useState<CountryOption[]>([])
  const [states, setStates] = useState<StateOption[]>([])
  const [loadingCountries, setLoadingCountries] = useState(false)
  const [loadingStates, setLoadingStates] = useState(false)

  // Track the country _id whose states are currently loaded
  const loadedForCountryId = useRef<string | null>(null)

  // ── Fetch countries once on mount ─────────────────────────────────────────
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true)
        const response = await api.get('state/country')
        setCountries(response.data || [])
      } catch (err) {
        console.error('LocationSelector: failed to fetch countries', err)
      } finally {
        setLoadingCountries(false)
      }
    }
    fetchCountries()
  }, [])

  // ── Fetch states for a given country _id ──────────────────────────────────
  const fetchStatesForId = useCallback(async (countryId: string) => {
    if (loadedForCountryId.current === countryId) return // already loaded
    try {
      setLoadingStates(true)
      const response = await api.get(`state/state/${countryId}`)
      setStates(response.data || [])
      loadedForCountryId.current = countryId
    } catch (err) {
      console.error('LocationSelector: failed to fetch states', err)
      setStates([])
    } finally {
      setLoadingStates(false)
    }
  }, [])

  // ── React to country prop changes (manual OR from Google Autocomplete) ────
  // IMPORTANT: we never call onCountryChange here — that would trigger the
  // parent's state-reset (`state: ''`).  Fuzzy resolution is handled internally
  // by `resolvedCountry` for display and `fetchStatesForId` for API calls.
  useEffect(() => {
    if (!country || countries.length === 0) {
      if (!country) {
        setStates([])
        loadedForCountryId.current = null
      }
      return
    }

    const matched = fuzzyFindCountry(country, countries)
    if (!matched) {
      // Country text from Google doesn't match any API entry — show it as-is
      setStates([])
      loadedForCountryId.current = null
      return
    }

    // Fetch states based on fuzzy-matched country _id
    fetchStatesForId(matched._id)
  }, [country, countries, fetchStatesForId])

  // ── React to state prop changes from Google Autocomplete ──────────────────
  // When states finish loading: if the current state value doesn't exactly
  // match any option, fuzzy-correct it
  useEffect(() => {
    if (!state || states.length === 0) return
    const matched = fuzzyFindState(state, states)
    if (matched && matched.name !== state) {
      onStateChange(matched.name)
    }
  }, [states, state, onStateChange])

  // ── Identify the resolved country value for the Select ───────────────────
  // The Select `value` must exactly match a MenuItem value.
  // If parent has a fuzzy name, find the matched API name; else use as-is.
  const resolvedCountry = React.useMemo(() => {
    if (!country || countries.length === 0) return country
    const matched = fuzzyFindCountry(country, countries)
    return matched ? matched.name : country
  }, [country, countries])

  const resolvedState = React.useMemo(() => {
    if (!state || states.length === 0) return state
    const matched = fuzzyFindState(state, states)
    return matched ? matched.name : state
  }, [state, states])

  // ── Whether the state select is ready ────────────────────────────────────
  const stateDisabled = disabled || !country || loadingStates

  // Does the current country value exist in the API list?
  const countryInList = countries.some(c => c.name === resolvedCountry)
  // Does the current state value exist in the loaded list?
  const stateInList = states.some(s => s.name === resolvedState)

  if (renderField === 'country') {
    return (
      <Box sx={{ width: '100%', ...sx }}>
        <Typography
          variant="body2"
          sx={{ color: 'grey.700', mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}
        >
          Country {required && '*'}
        </Typography>
        <FormControl fullWidth size="small" error={!!countryError}>
          <Select
            displayEmpty
            value={resolvedCountry}
            disabled={disabled || loadingCountries}
            onChange={e => {
              onCountryChange(e.target.value as string)
              onStateChange('')
              loadedForCountryId.current = null
              setStates([])
            }}
            sx={selectSx(accentColor, disabled)}
            endAdornment={
              loadingCountries ? (
                <CircularProgress size={16} sx={{ mr: 2, color: 'grey.500' }} />
              ) : undefined
            }
          >
            <MenuItem value="">
              <em>{loadingCountries ? 'Loading countries...' : 'Select Country'}</em>
            </MenuItem>

            {resolvedCountry && !countryInList && !loadingCountries && (
              <MenuItem value={resolvedCountry}>{resolvedCountry}</MenuItem>
            )}

            {countries.map(c => (
              <MenuItem key={c._id} value={c.name}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
          {countryError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {countryError}
            </Typography>
          )}
        </FormControl>
      </Box>
    )
  }

  if (renderField === 'state') {
    return (
      <Box sx={{ width: '100%', ...sx }}>
        <Typography
          variant="body2"
          sx={{ color: 'grey.700', mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}
        >
          State {required && '*'}
        </Typography>
        <FormControl fullWidth size="small" error={!!stateError}>
          <Select
            displayEmpty
            value={resolvedState}
            disabled={stateDisabled}
            onChange={e => onStateChange(e.target.value as string)}
            sx={selectSx(accentColor, stateDisabled)}
            endAdornment={
              loadingStates ? (
                <CircularProgress size={16} sx={{ mr: 2, color: 'grey.500' }} />
              ) : undefined
            }
          >
            <MenuItem value="">
              <em>
                {!country
                  ? 'Select a country first'
                  : loadingStates
                  ? 'Loading states...'
                  : 'Select State'}
              </em>
            </MenuItem>

            {resolvedState && !stateInList && !loadingStates && (
              <MenuItem value={resolvedState}>{resolvedState}</MenuItem>
            )}

            {states.map(s => (
              <MenuItem key={s._id} value={s.name}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
          {stateError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {stateError}
            </Typography>
          )}
        </FormControl>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        width: '100%',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        ...sx,
      }}
    >
      {/* ── Country ───────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ color: 'grey.700', mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}
        >
          Country {required && '*'}
        </Typography>
        <FormControl fullWidth size="small" error={!!countryError}>
          <Select
            displayEmpty
            value={resolvedCountry}
            disabled={disabled || loadingCountries}
            onChange={e => {
              onCountryChange(e.target.value as string)
              onStateChange('')
              loadedForCountryId.current = null
              setStates([])
            }}
            sx={selectSx(accentColor, disabled)}
            endAdornment={
              loadingCountries ? (
                <CircularProgress size={16} sx={{ mr: 2, color: 'grey.500' }} />
              ) : undefined
            }
          >
            <MenuItem value="">
              <em>{loadingCountries ? 'Loading countries...' : 'Select Country'}</em>
            </MenuItem>

            {/* Fallback: show Google-provided value even if not in API list */}
            {resolvedCountry && !countryInList && !loadingCountries && (
              <MenuItem value={resolvedCountry}>{resolvedCountry}</MenuItem>
            )}

            {countries.map(c => (
              <MenuItem key={c._id} value={c.name}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
          {countryError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {countryError}
            </Typography>
          )}
        </FormControl>
      </Box>

      {/* ── State ─────────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ color: 'grey.700', mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}
        >
          State {required && '*'}
        </Typography>
        <FormControl fullWidth size="small" error={!!stateError}>
          <Select
            displayEmpty
            value={resolvedState}
            disabled={stateDisabled}
            onChange={e => onStateChange(e.target.value as string)}
            sx={selectSx(accentColor, stateDisabled)}
            endAdornment={
              loadingStates ? (
                <CircularProgress size={16} sx={{ mr: 2, color: 'grey.500' }} />
              ) : undefined
            }
          >
            <MenuItem value="">
              <em>
                {!country
                  ? 'Select a country first'
                  : loadingStates
                  ? 'Loading states...'
                  : 'Select State'}
              </em>
            </MenuItem>

            {/* Fallback: show Google-provided state even if not in API list yet */}
            {resolvedState && !stateInList && !loadingStates && (
              <MenuItem value={resolvedState}>{resolvedState}</MenuItem>
            )}

            {states.map(s => (
              <MenuItem key={s._id} value={s.name}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
          {stateError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {stateError}
            </Typography>
          )}
        </FormControl>
      </Box>
    </Box>
  )
}

export default LocationSelector
